import {
  RunAgentInput,
  Message,
  ToolCall,
  BaseEvent,
  EventType,
  RunStartedEvent,
  RunFinishedEvent,
  RunErrorEvent,
  TextMessageStartEvent,
  TextMessageContentEvent,
  TextMessageEndEvent,
  ToolCallStartEvent,
  ToolCallArgsEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  AbstractAgent,
  AgentConfig,
  BinaryInputContent,
} from "@ag-ui/client";

import { Message as LangGraphMessage } from "@langchain/langgraph-sdk";

import { Observable, Subscriber } from "rxjs";

import {
  Annotation,
  AnnotationRoot,
  StateDefinition,
  StateGraph,
  MessagesAnnotation,
} from "@langchain/langgraph";
import { InteropZodObject } from "@langchain/core/utils/types";
import { convertActionsToDynamicStructuredTools } from "./util";
import { Logger, noopLogger, isErrorWithCode } from "@cloudbase/agent-shared";

// Import observability callback (optional)
// The class reference is shared across all agent instances (lazy-loaded once)
// but each agent creates its own handler instance
let LangChainCallbackHandler: any;

type SDZod = StateDefinition | InteropZodObject;

type CompiledStateGraph<SD extends SDZod> = ReturnType<
  StateGraph<SD>["compile"]
>;

type AnnotationInside<T> = T extends AnnotationRoot<infer U> ? U : never;

type ClientStateDefinition = AnnotationInside<typeof ClientStateAnnotation>;

export const ClientPropertiesAnnotation = Annotation.Root({
  tools: Annotation<any[]>,
});

export const ClientStateAnnotation = Annotation.Root({
  client: Annotation<typeof ClientPropertiesAnnotation.State>,
  ...MessagesAnnotation.spec,
});

export type ClientState = typeof ClientStateAnnotation.State;

export class LanggraphAgent extends AbstractAgent {
  compiledWorkflow?: CompiledStateGraph<ClientStateDefinition>;
  private observabilityCallback?: any;
  private adapterName: string;
  private logger: Logger;

  constructor(
    agentConfig: AgentConfig & {
      compiledWorkflow: any;
      adapterName?: string; // Optional: e.g., "LangGraph" or "LangChain"
      /**
       * Logger instance for structured logging.
       * @default noopLogger (silent)
       */
      logger?: Logger;
    }
  ) {
    super(agentConfig);
    this.compiledWorkflow =
      agentConfig.compiledWorkflow as CompiledStateGraph<ClientStateDefinition>;
    // Observability callback will be lazily loaded in _run method
    this.adapterName = agentConfig.adapterName || "LangGraph";

    // Create component-specific child logger
    const baseLogger = agentConfig.logger ?? noopLogger;
    this.logger =
      baseLogger.child?.({ component: "langgraph-agent" }) ?? baseLogger;
  }

  run(input: RunAgentInput) {
    return new Observable<BaseEvent>((subscriber) => {
      this._run(subscriber, input);
    });
  }

  async _run(subscriber: Subscriber<BaseEvent>, input: RunAgentInput) {
    const { messages, runId, threadId } = input;

    // Create run-specific child logger with context
    const logger = this.logger.child?.({ runId, threadId }) ?? this.logger;

    logger.info?.("Run started");

    // Setup observability for this execution
    await this.setupObservability(input, logger);

    const runStartedEvent: RunStartedEvent = {
      type: EventType.RUN_STARTED,
      threadId,
      runId,
    };
    logger.trace?.({ aguiEvent: runStartedEvent }, "Emitting AGUI event");
    subscriber.next(runStartedEvent);

    // DEBUG: summary of input
    const lastUserMessage = messages.filter((m) => m.role === "user").pop();
    logger.debug?.(
      {
        messageCount: messages.length,
        toolCount: input.tools?.length ?? 0,
        tools: input.tools?.map((t) => t.name),
        lastUserMessage:
          typeof lastUserMessage?.content === "string"
            ? lastUserMessage.content.slice(0, 200)
            : undefined,
      },
      "Preparing stream input"
    );

    // TRACE: full input
    logger.trace?.({ messages, tools: input.tools }, "Full input messages");

    // Convert messages to LangGraph format
    const langChainMessages = aguiMessagesToLangChain(messages, logger);

    // TRACE: converted LangGraph messages for debugging message ordering issues
    logger.trace?.(
      {
        langChainMessages,
        messageCount: langChainMessages.length,
        messageTypes: langChainMessages.map((m) => ({
          type: m.type,
          id: m.id,
          hasToolCalls:
            "tool_calls" in m &&
            Array.isArray(m.tool_calls) &&
            m.tool_calls.length > 0,
          toolCallId: "tool_call_id" in m ? m.tool_call_id : undefined,
        })),
      },
      "Converted LangGraph messages"
    );

    const streamEventInput = {
      messages: langChainMessages,
      client: {
        tools: convertActionsToDynamicStructuredTools(
          input.tools.map((x) => ({
            ...x,
            parameters:
              typeof x.parameters === "string"
                ? JSON.parse(x.parameters)
                : x.parameters,
          }))
        ),
      },
    };

    const stream = this.compiledWorkflow!.streamEvents(streamEventInput, {
      version: "v2",
      ...(this.observabilityCallback
        ? { callbacks: [this.observabilityCallback] }
        : {}),
      runId,
      configurable: {
        thread_id: threadId,
      },
    });

    logger.debug?.("Stream created, starting event processing");

    const chatModelRuns: Array<{ runId: string; messageId?: string }> = [];

    // Pre-populate handledToolCallIds with tool call IDs from INPUT messages.
    // This prevents emitting duplicate TOOL_CALL_RESULT events for tool results
    // that were already processed in a previous run and are now being sent back
    // by the client as part of the message history.
    const handledToolCallIds: Set<string> = new Set();
    for (const msg of messages) {
      if (msg.role === "tool" && msg.toolCallId) {
        handledToolCallIds.add(msg.toolCallId);
      }
    }
    if (handledToolCallIds.size > 0) {
      logger.debug?.(
        { count: handledToolCallIds.size },
        "Pre-populated handled tool call IDs from input messages"
      );
    }

    let currentToolCall: { name: string; args: string; id: string } | null =
      null;

    // Statistics for run summary
    let eventCount = 0;
    let toolCallCount = 0;
    let textChunkCount = 0;

    try {
      for await (const event of stream) {
        eventCount++;
        // TRACE: full LangGraph event
        logger.trace?.(
          { eventType: event.event, eventCount, langGraphEvent: event },
          "Processing stream event"
        );

        if (event.event.startsWith("ChannelWrite<")) {
          continue;
        }

        if (event.event === "on_chat_model_start") {
          logger.debug?.(
            { chatModelRunId: event.run_id },
            "Chat model started"
          );
          chatModelRuns.push({ runId: event.run_id });
          continue;
        }

        if (event.event === "on_chat_model_stream") {
          // First, find or create chatModelRun and ensure messageId is set
          // This must happen BEFORE processing tool_call_chunks so we have parentMessageId
          const chatModelRun = chatModelRuns.find(
            (run) => run.runId === event.run_id
          );
          if (!chatModelRun) {
            logger.warn?.(
              { chatModelRunId: event.run_id },
              "Received message from unknown chat model run"
            );
            subscriber.next({
              type: EventType.RUN_ERROR,
              code: "INTERNAL_ERROR",
              message: `Received a message from an unknown chat model run. Run Id: ${event.run_id}`,
            } as RunErrorEvent);
            continue;
          }

          // Initialize messageId if not set yet
          const chunkId = event.data.chunk.id;
          if (!chatModelRun.messageId) {
            chatModelRun.messageId = chunkId;
            const textStartEvent: TextMessageStartEvent = {
              messageId: chunkId,
              type: EventType.TEXT_MESSAGE_START,
              role: "assistant",
            };
            logger.debug?.({ messageId: chunkId }, "Text message started");
            logger.trace?.(
              { aguiEvent: textStartEvent },
              "Emitting AGUI event"
            );
            subscriber.next(textStartEvent);
          } else if (chatModelRun.messageId !== chunkId) {
            logger.warn?.(
              {
                expectedMessageId: chatModelRun.messageId,
                receivedMessageId: chunkId,
                chatModelRunId: event.run_id,
              },
              "Received message with unexpected ID"
            );
            subscriber.next({
              type: EventType.RUN_ERROR,
              code: "INTERNAL_ERROR",
              message: `Received a message of unknown message id from current run. Run Id: ${event.run_id} Message Id from current run: ${chatModelRun.messageId} Message Id from received message: ${chunkId}`,
            } as RunErrorEvent);
            continue;
          }

          // Now process tool_call_chunks with parentMessageId available
          if (
            Array.isArray(event.data.chunk?.tool_call_chunks) &&
            event.data.chunk?.tool_call_chunks?.length > 0
          ) {
            const parentMessageId = chatModelRun.messageId;
            (
              event.data.chunk.tool_call_chunks as Array<{
                name: string;
                args: unknown;
                id: string;
              }>
            )
              .map((x) => ({
                ...x,
                args:
                  typeof x.args === "string"
                    ? x.args
                    : x.args
                      ? JSON.stringify(x.args)
                      : "",
              }))
              .forEach((toolCall) => {
                if (currentToolCall) {
                  if (toolCall.id && currentToolCall.id !== toolCall.id) {
                    // end the current tool call
                    const toolEndEvent: ToolCallEndEvent = {
                      toolCallId: currentToolCall.id,
                      type: EventType.TOOL_CALL_END,
                    };
                    logger.debug?.(
                      {
                        toolCallId: currentToolCall.id,
                        toolCallName: currentToolCall.name,
                      },
                      "Tool call ended"
                    );
                    logger.trace?.(
                      { aguiEvent: toolEndEvent },
                      "Emitting AGUI event"
                    );
                    subscriber.next(toolEndEvent);

                    // set the new tool call as the current tool call
                    if (toolCall.name && toolCall.id) {
                      currentToolCall = toolCall;
                      toolCallCount++;
                      const toolStartEvent: ToolCallStartEvent = {
                        toolCallId: currentToolCall.id,
                        toolCallName: currentToolCall.name,
                        parentMessageId,
                        type: EventType.TOOL_CALL_START,
                      };
                      logger.debug?.(
                        {
                          toolCallId: toolCall.id,
                          toolCallName: toolCall.name,
                        },
                        "Tool call started"
                      );
                      logger.trace?.(
                        { aguiEvent: toolStartEvent },
                        "Emitting AGUI event"
                      );
                      subscriber.next(toolStartEvent);
                      if (currentToolCall.args) {
                        const toolArgsEvent: ToolCallArgsEvent = {
                          toolCallId: currentToolCall.id,
                          delta: currentToolCall.args,
                          type: EventType.TOOL_CALL_ARGS,
                        };
                        logger.trace?.(
                          { aguiEvent: toolArgsEvent },
                          "Emitting AGUI event"
                        );
                        subscriber.next(toolArgsEvent);

                        if (isValidJson(currentToolCall.args)) {
                          // args is valid json, tool call streaming ends
                          const toolEndEvent2: ToolCallEndEvent = {
                            toolCallId: currentToolCall.id,
                            type: EventType.TOOL_CALL_END,
                          };
                          logger.debug?.(
                            { toolCallId: currentToolCall.id },
                            "Tool call ended (args complete)"
                          );
                          logger.trace?.(
                            { aguiEvent: toolEndEvent2 },
                            "Emitting AGUI event"
                          );
                          subscriber.next(toolEndEvent2);
                          currentToolCall = null;
                        }
                      }
                    }
                  } else {
                    // update the current tool call
                    if (toolCall.args) {
                      currentToolCall.args += toolCall.args;
                      const toolArgsEvent: ToolCallArgsEvent = {
                        toolCallId: currentToolCall.id,
                        delta: toolCall.args,
                        type: EventType.TOOL_CALL_ARGS,
                      };
                      logger.trace?.(
                        { aguiEvent: toolArgsEvent },
                        "Emitting AGUI event"
                      );
                      subscriber.next(toolArgsEvent);

                      if (isValidJson(currentToolCall.args)) {
                        // args is valid json, tool call streaming ends
                        const toolEndEvent: ToolCallEndEvent = {
                          toolCallId: currentToolCall.id,
                          type: EventType.TOOL_CALL_END,
                        };
                        logger.debug?.(
                          { toolCallId: currentToolCall.id },
                          "Tool call ended (args complete)"
                        );
                        logger.trace?.(
                          { aguiEvent: toolEndEvent },
                          "Emitting AGUI event"
                        );
                        subscriber.next(toolEndEvent);
                        currentToolCall = null;
                      }
                    }
                  }
                } else {
                  if (toolCall.name && toolCall.id) {
                    currentToolCall = toolCall;
                    toolCallCount++;
                    const toolStartEvent: ToolCallStartEvent = {
                      toolCallId: toolCall.id,
                      toolCallName: toolCall.name,
                      parentMessageId,
                      type: EventType.TOOL_CALL_START,
                    };
                    logger.debug?.(
                      { toolCallId: toolCall.id, toolCallName: toolCall.name },
                      "Tool call started"
                    );
                    logger.trace?.(
                      { aguiEvent: toolStartEvent },
                      "Emitting AGUI event"
                    );
                    subscriber.next(toolStartEvent);
                    if (toolCall.args) {
                      const toolArgsEvent: ToolCallArgsEvent = {
                        toolCallId: toolCall.id,
                        delta: toolCall.args,
                        type: EventType.TOOL_CALL_ARGS,
                      };
                      logger.trace?.(
                        { aguiEvent: toolArgsEvent },
                        "Emitting AGUI event"
                      );
                      subscriber.next(toolArgsEvent);

                      if (isValidJson(toolCall.args)) {
                        // args is valid json, tool call streaming ends
                        const toolEndEvent: ToolCallEndEvent = {
                          toolCallId: toolCall.id,
                          type: EventType.TOOL_CALL_END,
                        };
                        logger.debug?.(
                          { toolCallId: toolCall.id },
                          "Tool call ended (args complete)"
                        );
                        logger.trace?.(
                          { aguiEvent: toolEndEvent },
                          "Emitting AGUI event"
                        );
                        subscriber.next(toolEndEvent);
                        currentToolCall = null;
                      }
                    }
                  }
                }
              });
          }

          // Process text content
          const delta = event.data.chunk.content;
          if (typeof delta === "string" && delta) {
            textChunkCount++;
            const textContentEvent: TextMessageContentEvent = {
              messageId: chatModelRun.messageId!,
              type: EventType.TEXT_MESSAGE_CONTENT,
              delta,
            };
            logger.trace?.(
              { aguiEvent: textContentEvent },
              "Emitting AGUI event"
            );
            subscriber.next(textContentEvent);
          }

          continue;
        }

        if (event.event === "on_chat_model_end") {
          const chatModelRun = chatModelRuns.find(
            (run) => run.runId === event.run_id
          );

          if (!chatModelRun) {
            logger.warn?.(
              { chatModelRunId: event.run_id },
              "Received on_chat_model_end from unknown run"
            );
            subscriber.next({
              type: EventType.RUN_ERROR,
              code: "INTERNAL_ERROR",
              message: `Received a on_chat_model_end event from an unknown chat model run. Run Id: ${event.run_id}`,
            } as RunErrorEvent);
            continue;
          }

          const textEndEvent: TextMessageEndEvent = {
            type: EventType.TEXT_MESSAGE_END,
            messageId: chatModelRun.messageId!,
          };
          logger.debug?.(
            { messageId: chatModelRun.messageId },
            "Text message ended"
          );
          logger.trace?.({ aguiEvent: textEndEvent }, "Emitting AGUI event");
          subscriber.next(textEndEvent);

          continue;
        }

        // Handle on_tool_end: Pre-assign ID to toolMessage BEFORE messagesStateReducer runs.
        // This ensures the same ID is used in:
        // 1. TOOL_CALL_RESULT event (sent to frontend)
        // 2. Checkpoint (stored in LangGraph state)
        // 3. Second request (frontend sends back the same ID for deduplication)
        if (event.event === "on_tool_end") {
          const toolMessage = event.data.output;
          if (toolMessage && toolMessage.tool_call_id) {
            if (!handledToolCallIds.has(toolMessage.tool_call_id)) {
              // Pre-assign ID before messagesStateReducer runs.
              // The reducer checks: if (m.id === null || m.id === undefined) m.id = v4()
              // By setting ID here, the reducer will skip assigning a new one.
              if (!toolMessage.id) {
                toolMessage.id = crypto.randomUUID();
                // Also set lc_kwargs.id to match what the reducer does
                if (toolMessage.lc_kwargs) {
                  toolMessage.lc_kwargs.id = toolMessage.id;
                }
              }

              const toolResultEvent: ToolCallResultEvent = {
                toolCallId: toolMessage.tool_call_id,
                type: EventType.TOOL_CALL_RESULT,
                content:
                  typeof toolMessage.content === "string"
                    ? toolMessage.content
                    : JSON.stringify(toolMessage.content),
                messageId: toolMessage.id,
              };
              logger.debug?.(
                {
                  toolCallId: toolMessage.tool_call_id,
                  messageId: toolMessage.id,
                },
                "Tool call result received"
              );
              logger.trace?.(
                { aguiEvent: toolResultEvent },
                "Emitting AGUI event"
              );
              subscriber.next(toolResultEvent);
              handledToolCallIds.add(toolMessage.tool_call_id);
            } else {
              logger.trace?.(
                { toolCallId: toolMessage.tool_call_id },
                "Skipping duplicate tool call result"
              );
            }
          }
          continue;
        }

        if (event.event === "on_chain_stream") {
          const chunk = event.data.chunk;

          // TRACE: state updates including messages for debugging ordering issues
          if (chunk?.messages && Array.isArray(chunk.messages)) {
            logger.trace?.(
              {
                nodeName: event.name,
                stateMessages: chunk.messages.map((m: LangGraphMessage) => ({
                  type: m.type,
                  id: m.id,
                  contentPreview:
                    typeof m.content === "string"
                      ? m.content.slice(0, 100)
                      : "[non-string content]",
                  hasToolCalls:
                    "tool_calls" in m &&
                    Array.isArray(m.tool_calls) &&
                    m.tool_calls.length > 0,
                  toolCallId: "tool_call_id" in m ? m.tool_call_id : undefined,
                })),
                messageCount: chunk.messages.length,
              },
              "State update with messages"
            );
          }
        }
      }

      const stats = { eventCount, toolCallCount, textChunkCount };

      const runFinishedEvent: RunFinishedEvent = {
        type: EventType.RUN_FINISHED,
        threadId,
        runId,
      };
      logger.info?.({ outcome: "complete", ...stats }, "Run finished");
      logger.trace?.({ aguiEvent: runFinishedEvent }, "Emitting AGUI event");
      subscriber.next(runFinishedEvent);
    } catch (error) {
      logger.error?.(
        { err: error, eventCount, toolCallCount, textChunkCount },
        "Error during stream processing"
      );

      // Extract code from error if available
      const errorCode = isErrorWithCode(error) ? error.code : "INTERNAL_ERROR";
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      subscriber.next({
        type: EventType.RUN_ERROR,
        code: errorCode,
        message: errorMessage,
      } as RunErrorEvent);
    }

    subscriber.complete();
  }

  clone() {
    const workflow = this.compiledWorkflow;
    const logger = this.logger;
    this.compiledWorkflow = undefined;

    const cloned = super.clone() as LanggraphAgent;

    this.compiledWorkflow = workflow;
    cloned.compiledWorkflow = workflow;
    cloned.logger = logger;

    return cloned;
  }

  /**
   * Setup observability for agent execution.
   * Lazy loads observability callback, restores server context from forwardedProps,
   * and configures the callback for graph execution.
   */
  private async setupObservability(
    input: RunAgentInput,
    logger: Logger
  ): Promise<void> {
    // Lazy load observability callback (shared across all instances)
    if (!LangChainCallbackHandler) {
      try {
        logger.debug?.("Attempting to load observability...");
        const obsModule = await import("@cloudbase/agent-observability/langchain");
        LangChainCallbackHandler = obsModule.CallbackHandler;
        logger.debug?.("✓ Observability handler loaded");
      } catch (e) {
        logger.debug?.(
          "✗ Observability not available:",
          e instanceof Error ? e.message : String(e)
        );
      }
    }

    // Use shared handler instance for this agent
    this.observabilityCallback = LangChainCallbackHandler
      ? new LangChainCallbackHandler({
          adapterName: this.adapterName,
          logger: logger,
        })
      : undefined;

    // Restore server SpanContext if available
    if (
      this.observabilityCallback &&
      input.forwardedProps?.__agui_server_context
    ) {
      try {
        const serverContextData = input.forwardedProps.__agui_server_context;

        // Reconstruct SpanContext from serialized data
        const serverSpanContext = {
          traceId: serverContextData.traceId,
          spanId: serverContextData.spanId,
          traceFlags: serverContextData.traceFlags,
          isRemote: true,
        };

        // Set external parent context in CallbackHandler with threadId/runId metadata
        this.observabilityCallback.setExternalParentContext(serverSpanContext, {
          threadId: input.threadId,
          runId: input.runId,
        });

        logger.debug?.("✓ Server context restored:", {
          traceId: serverSpanContext.traceId,
          spanId: serverSpanContext.spanId,
          threadId: input.threadId,
          runId: input.runId,
        });
      } catch (e) {
        logger.debug?.("Failed to restore server context:", e);
      }
    }
  }
}

function aguiMessagesToLangChain(
  messages: Message[],
  logger?: Logger
): LangGraphMessage[] {
  return messages.map((message) => {
    switch (message.role) {
      case "user":
        if (typeof message.content === "string") {
          return {
            id: message.id,
            role: message.role,
            content: message.content,
            type: "human",
          };
        } else {
          // Convert AG-UI InputContent[] to LangChain multimodal format
          const langChainContent: Array<
            | { type: "text"; text: string }
            | { type: "image_url"; image_url: { url: string } }
          > = [];

          for (const part of message.content) {
            if (part.type === "text") {
              langChainContent.push({ type: "text", text: part.text });
            } else if (part.type === "binary") {
              const imageUrl = convertBinaryToImageUrl(
                part,
                message.id,
                logger
              );
              if (imageUrl) {
                langChainContent.push({
                  type: "image_url",
                  image_url: { url: imageUrl },
                });
              }
            }
          }

          if (langChainContent.length === 0) {
            logger?.warn?.(
              { messageId: message.id },
              "User message content array resulted in empty LangChain content"
            );
          }

          return {
            id: message.id,
            role: message.role,
            content: langChainContent,
            type: "human",
          };
        }

      case "assistant":
        return {
          id: message.id,
          type: "ai",
          role: message.role,
          content: message.content ?? "",
          tool_calls: (message.toolCalls ?? []).map((tc: ToolCall) => ({
            id: tc.id,
            name: tc.function.name,
            args: JSON.parse(tc.function.arguments),
            type: "tool_call",
          })),
        };
      case "system":
        return {
          id: message.id,
          role: message.role,
          content: message.content,
          type: "system",
        };
      case "tool":
        return {
          content: message.content,
          role: message.role,
          type: message.role,
          tool_call_id: message.toolCallId,
          id: message.id,
        };
      default:
        // Note: Can't use this.logger here as this is a standalone function
        // The error will be caught and logged by the caller
        throw new Error(`Message role ${message.role} is not supported.`);
    }
  });
}

function isValidJson(json: string) {
  try {
    JSON.parse(json);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Converts a BinaryInputContent to a LangChain image_url format.
 * Only supports image/* mimeTypes.
 *
 * @returns The image URL string, or undefined if:
 *   - mimeType is not an image type
 *   - Only `id` is provided (requires external resolution, not supported)
 *   - No valid payload (url/data) is available
 */
function convertBinaryToImageUrl(
  part: BinaryInputContent,
  messageId: string,
  logger?: Logger
): string | undefined {
  // Only support image types
  if (!part.mimeType.startsWith("image/")) {
    logger?.debug?.(
      { mimeType: part.mimeType, messageId },
      "Skipping non-image binary content"
    );
    return undefined;
  }

  // Prefer url, then data URL
  if (part.url) {
    return part.url;
  }

  if (part.data) {
    return `data:${part.mimeType};base64,${part.data}`;
  }

  return undefined;
}
